import { computed, toRef } from "vue";
import type { Descriptor, Module } from "@/types/domain";
import type { DescriptorVM, ModuleVM } from "@/types/view";

export function buildModuleVM(t: Module): ModuleVM {
  return {
    ...t,
    descriptors: (t.descriptors ?? [])
      .sort((a, b) => a.order - b.order)
      .map(d => toVM(d)),
  };
}

function toVM(d: Descriptor): DescriptorVM {
  const children = (d.descriptors ?? [])
    .sort((a, b) => a.order - b.order)
    .map(c => toVM(c));

  const statementCount = countStatements(d);
  const statementRefs = toRef(d.statements);

  // evaluated statements in THIS descriptor only
  const _localEvaluated = computed(() =>
    (statementRefs.value ?? []).filter(s => s.evaluation != null).length
  );

  //Evaluated in THIS node only
  const _evaluatedStatementCount = computed(() => {
    const childSum = children.reduce((acc, c) => acc + c.evaluatedStatementCount, 0);
    return childSum + _localEvaluated.value;
  });

  // Weighted average across evaluated statements + child averages
  const _averageEvaluation = computed<number>(() => {
    // local statements
    const loc = statementRefs.value ?? [];
    const locVals = loc.filter(s => s.evaluation != null).map(s => s.evaluation as number);
    const locCount = locVals.length;
    const locSum = locVals.reduce((a, b) => a + b, 0);

    // children as buckets: weight by their evaluated count
    let childSum = 0;
    let childCount = 0;
    for (const c of children) {
      const cnt = c.evaluatedStatementCount;
      if (cnt > 0) {
        childSum += (c.averageEvaluation * cnt);
        childCount += cnt;
      }
    }

    const totalCount = locCount + childCount;
    if (totalCount === 0) return 0;

    return (locSum + childSum) / totalCount;
  });

  const _lowestEvaluation = computed<number>(() => {
    // consider lowest across either children with answers or local answered statements
    const childMins: number[] = [];
    for (const c of children) {
      if (c.evaluatedStatementCount > 0) childMins.push(c.averageEvaluation);
    }

    const localVals = (statementRefs.value ?? [])
      .filter(s => s.evaluation != null)
      .map(s => s.evaluation as number);

    const all = [...childMins, ...localVals];
    if (all.length === 0) return 0;
    return all.reduce((a, b) => Math.min(a, b), Number.POSITIVE_INFINITY);
  });


  const vm: DescriptorVM = {
    ...d,
    descriptors: children,

    // getters: TS sees numbers; Vue tracks inner computeds
    get evaluatedStatementCount() { return _evaluatedStatementCount.value },
    get averageEvaluation()       { return _averageEvaluation.value },
    get lowestEvaluation()        { return _lowestEvaluation.value },

    statementCount,
  }

  return vm
}

function countStatements(d: Descriptor): number {
  const here = (d.statements ?? []).length;
  const children = (d.descriptors ?? []).reduce((acc, c) => acc + countStatements(c), 0);
  return here + children;
}
